import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  getAuthToken,
  getCurrentShopId,
  isLoggedIn,
} from "./auth";
import "./PromotionsPage.css";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.deskpos.ie";

const getTodayDate = () => {
  return new Date().toISOString().slice(0, 10);
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const cleanValue = String(value).slice(0, 10);

  const date = new Date(`${cleanValue}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return cleanValue;
  }

  return date.toLocaleDateString("en-IE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatMoney = (value) => {
  return `€${Number(value || 0).toFixed(2)}`;
};

const normalizePromotionStatus = (promotion) => {
  const backendStatus = String(
    promotion?.status || "",
  )
    .trim()
    .toLowerCase();

  if (
    ["active", "upcoming", "expired", "inactive"].includes(
      backendStatus,
    )
  ) {
    return backendStatus;
  }

  if (
    promotion?.active === false ||
    promotion?.active === 0 ||
    promotion?.active === "false"
  ) {
    return "inactive";
  }

  const today = getTodayDate();
  const startDate = String(
    promotion?.start_date || "",
  ).slice(0, 10);
  const endDate = String(
    promotion?.end_date || "",
  ).slice(0, 10);

  if (startDate && today < startDate) {
    return "upcoming";
  }

  if (endDate && today > endDate) {
    return "expired";
  }

  return "active";
};

function PromotionsPage() {
  const navigate = useNavigate();

  const [shopId, setShopId] = useState(null);

  const [products, setProducts] = useState([]);
  const [promotions, setPromotions] = useState([]);

  const [productSearch, setProductSearch] = useState("");
  const [promotionSearch, setPromotionSearch] = useState("");

  const [selectedProduct, setSelectedProduct] =
    useState(null);

  const [editingPromotionId, setEditingPromotionId] =
    useState(null);

  const [form, setForm] = useState({
    promo_price: "",
    start_date: getTodayDate(),
    end_date: getTodayDate(),
    active: true,
  });

  const [loadingProducts, setLoadingProducts] =
    useState(false);

  const [loadingPromotions, setLoadingPromotions] =
    useState(false);

  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState("");
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] =
    useState("");

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/admin-login");
      return;
    }

    const currentShopId = getCurrentShopId();

    if (!currentShopId) {
      setPageError(
        "Shop ID was not found. Please logout and login again.",
      );

      return;
    }

    setShopId(currentShopId);
  }, [navigate]);

  useEffect(() => {
    if (!shopId) {
      return;
    }

    fetchProducts();
    fetchPromotions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const getRequestConfig = () => {
    const token = getAuthToken();

    return {
      params: {
        shop_id: shopId,
      },

      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const fetchProducts = async () => {
    if (!shopId) {
      return;
    }

    try {
      setLoadingProducts(true);

      const response = await axios.get(
        `${API_BASE_URL}/products`,
        getRequestConfig(),
      );

      setProducts(
        Array.isArray(response.data)
          ? response.data
          : [],
      );
    } catch (error) {
      console.error(
        "Fetch promotion products error:",
        error.response?.data || error.message,
      );

      setProducts([]);

      setPageError(
        error.response?.data?.error ||
          "Products could not be loaded.",
      );
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchPromotions = async () => {
    if (!shopId) {
      return;
    }

    try {
      setLoadingPromotions(true);
      setPageError("");

      const response = await axios.get(
        `${API_BASE_URL}/promotions`,
        getRequestConfig(),
      );

      setPromotions(
        Array.isArray(response.data)
          ? response.data
          : [],
      );
    } catch (error) {
      console.error(
        "Fetch promotions error:",
        error.response?.data || error.message,
      );

      setPromotions([]);

      setPageError(
        error.response?.data?.error ||
          "Promotions could not be loaded.",
      );
    } finally {
      setLoadingPromotions(false);
    }
  };

  const filteredProducts = useMemo(() => {
    const cleanSearch = productSearch
      .trim()
      .toLowerCase();

    if (!cleanSearch) {
      return [];
    }

    return products
      .filter((product) => {
        const name = String(
          product.name || "",
        ).toLowerCase();

        const barcode = String(
          product.barcode || "",
        ).toLowerCase();

        return (
          name.includes(cleanSearch) ||
          barcode.includes(cleanSearch)
        );
      })
      .slice(0, 20);
  }, [products, productSearch]);

  const filteredPromotions = useMemo(() => {
    const cleanSearch = promotionSearch
      .trim()
      .toLowerCase();

    if (!cleanSearch) {
      return promotions;
    }

    return promotions.filter((promotion) => {
      const productName = String(
        promotion.product_name || "",
      ).toLowerCase();

      const barcode = String(
        promotion.barcode || "",
      ).toLowerCase();

      const status =
        normalizePromotionStatus(promotion);

      return (
        productName.includes(cleanSearch) ||
        barcode.includes(cleanSearch) ||
        status.includes(cleanSearch)
      );
    });
  }, [promotions, promotionSearch]);

  const resetForm = () => {
    setSelectedProduct(null);
    setEditingPromotionId(null);
    setProductSearch("");

    setForm({
      promo_price: "",
      start_date: getTodayDate(),
      end_date: getTodayDate(),
      active: true,
    });

    setFormError("");
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);

    setProductSearch(
      `${product.name || "Product"}${
        product.barcode
          ? ` — ${product.barcode}`
          : ""
      }`,
    );

    setFormError("");
  };

  const validatePromotion = () => {
    if (!selectedProduct?.id) {
      return "Please select a product.";
    }

    const promoPrice = Number(form.promo_price);
    const normalPrice = Number(
      selectedProduct.price || 0,
    );

    if (
      !Number.isFinite(promoPrice) ||
      promoPrice <= 0
    ) {
      return "Promotion price must be greater than €0.";
    }

    if (promoPrice >= normalPrice) {
      return "Promotion price must be lower than the normal selling price.";
    }

    if (!form.start_date || !form.end_date) {
      return "Start date and end date are required.";
    }

    if (form.end_date < form.start_date) {
      return "End date cannot be before the start date.";
    }

    return "";
  };

  const savePromotion = async (event) => {
    event.preventDefault();

    if (!shopId) {
      setFormError(
        "Shop ID was not found. Please login again.",
      );

      return;
    }

    const validationError = validatePromotion();

    if (validationError) {
      setFormError(validationError);
      return;
    }

    const payload = {
      shop_id: shopId,
      product_id: Number(selectedProduct.id),
      promo_price: Number(form.promo_price),
      start_date: form.start_date,
      end_date: form.end_date,
      active: form.active === true,
    };

    try {
      setSaving(true);
      setFormError("");
      setSuccessMessage("");

      const token = getAuthToken();

      if (editingPromotionId) {
        await axios.put(
          `${API_BASE_URL}/promotions/${editingPromotionId}`,
          payload,
          {
            params: {
              shop_id: shopId,
            },

            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        setSuccessMessage(
          "Promotion updated successfully.",
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/promotions`,
          payload,
          {
            params: {
              shop_id: shopId,
            },

            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        setSuccessMessage(
          "Promotion created successfully.",
        );
      }

      resetForm();
      await fetchPromotions();

      setTimeout(() => {
        setSuccessMessage("");
      }, 3500);
    } catch (error) {
      console.error(
        "Save promotion error:",
        error.response?.data || error.message,
      );

      setFormError(
        error.response?.data?.error ||
          "Promotion could not be saved.",
      );
    } finally {
      setSaving(false);
    }
  };

  const editPromotion = (promotion) => {
    const matchingProduct = products.find(
      (product) =>
        Number(product.id) ===
        Number(promotion.product_id),
    );

    const productForEditing =
      matchingProduct || {
        id: promotion.product_id,
        name: promotion.product_name,
        barcode: promotion.barcode,
        price: promotion.normal_price,
        category: promotion.category,
      };

    setSelectedProduct(productForEditing);

    setEditingPromotionId(
      Number(promotion.id),
    );

    setProductSearch(
      `${promotion.product_name || "Product"}${
        promotion.barcode
          ? ` — ${promotion.barcode}`
          : ""
      }`,
    );

    setForm({
      promo_price: String(
        promotion.promo_price ?? "",
      ),

      start_date: String(
        promotion.start_date || "",
      ).slice(0, 10),

      end_date: String(
        promotion.end_date || "",
      ).slice(0, 10),

      active:
        promotion.active === true ||
        promotion.active === 1 ||
        promotion.active === "true",
    });

    setFormError("");
    setSuccessMessage("");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const deletePromotion = async (promotion) => {
    const confirmed = window.confirm(
      `Delete this promotion?\n\n` +
        `Product: ${
          promotion.product_name || "Product"
        }\n` +
        `Promotion price: ${formatMoney(
          promotion.promo_price,
        )}\n` +
        `Period: ${formatDate(
          promotion.start_date,
        )} to ${formatDate(
          promotion.end_date,
        )}\n\n` +
        `This action cannot be undone.`,
    );

    if (!confirmed) {
      return;
    }

    try {
      const token = getAuthToken();

      await axios.delete(
        `${API_BASE_URL}/promotions/${promotion.id}`,
        {
          params: {
            shop_id: shopId,
          },

          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (
        Number(editingPromotionId) ===
        Number(promotion.id)
      ) {
        resetForm();
      }

      setSuccessMessage(
        "Promotion deleted successfully.",
      );

      await fetchPromotions();

      setTimeout(() => {
        setSuccessMessage("");
      }, 3500);
    } catch (error) {
      console.error(
        "Delete promotion error:",
        error.response?.data || error.message,
      );

      setPageError(
        error.response?.data?.error ||
          "Promotion could not be deleted.",
      );
    }
  };

  return (
    <div className="promotions-page">
      <div className="promotions-page-header">
        <div>
          <h1>Promotions</h1>

          <p>
            Create scheduled promotional prices for
            products in this shop.
          </p>
        </div>

        <div className="promotions-header-count">
          <span>Promotions</span>
          <strong>{promotions.length}</strong>
        </div>
      </div>

      {successMessage && (
        <div className="promotion-success-message">
          ✓ {successMessage}
        </div>
      )}

      {pageError && (
        <div className="promotion-page-error">
          <span>{pageError}</span>

          <button
            type="button"
            onClick={() => {
              setPageError("");
              fetchProducts();
              fetchPromotions();
            }}
          >
            Try Again
          </button>
        </div>
      )}

      <section className="promotion-create-card">
        <div className="promotion-card-header">
          <div>
            <h2>
              {editingPromotionId
                ? "Edit Promotion"
                : "Create Promotion"}
            </h2>

            <p>
              Select a product and choose the promotional
              price and date range.
            </p>
          </div>

          {editingPromotionId && (
            <button
              type="button"
              className="promotion-cancel-edit-btn"
              onClick={resetForm}
            >
              Cancel Edit
            </button>
          )}
        </div>

        <form
          className="promotion-form"
          onSubmit={savePromotion}
        >
          <div className="promotion-form-field promotion-product-field">
            <label htmlFor="promotion-product-search">
              Search Product <span>*</span>
            </label>

            <input
              id="promotion-product-search"
              type="text"
              placeholder="Search by product name or barcode"
              value={productSearch}
              disabled={Boolean(editingPromotionId)}
              onChange={(event) => {
                setProductSearch(
                  event.target.value,
                );

                setSelectedProduct(null);
                setFormError("");
              }}
            />

            {!editingPromotionId &&
              productSearch.trim() &&
              !selectedProduct && (
                <div className="promotion-product-results">
                  {loadingProducts ? (
                    <div className="promotion-product-empty">
                      Loading products...
                    </div>
                  ) : filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                      <button
                        type="button"
                        className="promotion-product-option"
                        key={product.id}
                        onClick={() =>
                          selectProduct(product)
                        }
                      >
                        <div>
                          <strong>
                            {product.name ||
                              "Unnamed Product"}
                          </strong>

                          <span>
                            Barcode:{" "}
                            {product.barcode || "-"}
                          </span>
                        </div>

                        <div className="promotion-product-option-price">
                          {formatMoney(product.price)}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="promotion-product-empty">
                      No matching products found.
                    </div>
                  )}
                </div>
              )}

            {selectedProduct && (
              <div className="selected-promotion-product">
                <div>
                  <span>Selected Product</span>

                  <strong>
                    {selectedProduct.name ||
                      "Unnamed Product"}
                  </strong>

                  <small>
                    Barcode:{" "}
                    {selectedProduct.barcode || "-"}
                  </small>
                </div>

                <div>
                  <span>Normal Price</span>

                  <strong>
                    {formatMoney(
                      selectedProduct.price,
                    )}
                  </strong>
                </div>
              </div>
            )}
          </div>

          <div className="promotion-form-field">
            <label htmlFor="promotion-price">
              Promotion Price (€) <span>*</span>
            </label>

            <input
              id="promotion-price"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={form.promo_price}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  promo_price: event.target.value,
                }))
              }
            />

            {selectedProduct &&
              form.promo_price !== "" && (
                <div className="promotion-saving-preview">
                  Customer saves{" "}
                  <strong>
                    {formatMoney(
                      Math.max(
                        0,
                        Number(
                          selectedProduct.price || 0,
                        ) -
                          Number(
                            form.promo_price || 0,
                          ),
                      ),
                    )}
                  </strong>
                </div>
              )}
          </div>

          <div className="promotion-form-field">
            <label htmlFor="promotion-start-date">
              Start Date <span>*</span>
            </label>

            <input
              id="promotion-start-date"
              type="date"
              value={form.start_date}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  start_date: event.target.value,
                }))
              }
            />
          </div>

          <div className="promotion-form-field">
            <label htmlFor="promotion-end-date">
              End Date <span>*</span>
            </label>

            <input
              id="promotion-end-date"
              type="date"
              min={form.start_date || undefined}
              value={form.end_date}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  end_date: event.target.value,
                }))
              }
            />
          </div>

          <div className="promotion-form-field promotion-active-field">
            <label>Promotion Status</label>

            <label className="promotion-switch-row">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    active: event.target.checked,
                  }))
                }
              />

              <span className="promotion-switch-control" />

              <span>
                {form.active
                  ? "Promotion enabled"
                  : "Promotion disabled"}
              </span>
            </label>
          </div>

          {formError && (
            <div className="promotion-form-error">
              {formError}
            </div>
          )}

          <div className="promotion-form-actions">
            <button
              type="button"
              className="promotion-reset-btn"
              onClick={resetForm}
              disabled={saving}
            >
              Clear
            </button>

            <button
              type="submit"
              className="promotion-save-btn"
              disabled={
                saving || !selectedProduct
              }
            >
              {saving
                ? "Saving Promotion..."
                : editingPromotionId
                  ? "Save Changes"
                  : "Create Promotion"}
            </button>
          </div>
        </form>
      </section>

      <section className="promotions-list-card">
        <div className="promotions-list-header">
          <div>
            <h2>Existing Promotions</h2>

            <p>
              View active, upcoming, expired and inactive
              promotions.
            </p>
          </div>

          <input
            type="text"
            className="promotion-list-search"
            placeholder="Search promotions..."
            value={promotionSearch}
            onChange={(event) =>
              setPromotionSearch(event.target.value)
            }
          />
        </div>

        {loadingPromotions ? (
          <div className="promotions-empty-state">
            Loading promotions...
          </div>
        ) : filteredPromotions.length === 0 ? (
          <div className="promotions-empty-state">
            <strong>No promotions found</strong>

            <span>
              Create your first product promotion using
              the form above.
            </span>
          </div>
        ) : (
          <>
            <div className="promotions-desktop-table-wrapper">
              <table className="promotions-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Normal</th>
                    <th>Promo</th>
                    <th>Saving</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredPromotions.map(
                    (promotion) => {
                      const status =
                        normalizePromotionStatus(
                          promotion,
                        );

                      const saving =
                        Number(
                          promotion.normal_price || 0,
                        ) -
                        Number(
                          promotion.promo_price || 0,
                        );

                      return (
                        <tr key={promotion.id}>
                          <td>
                            <strong>
                              {promotion.product_name ||
                                "Unnamed Product"}
                            </strong>

                            <span className="promotion-table-barcode">
                              {promotion.barcode || "-"}
                            </span>
                          </td>

                          <td>
                            {formatMoney(
                              promotion.normal_price,
                            )}
                          </td>

                          <td className="promotion-price-cell">
                            {formatMoney(
                              promotion.promo_price,
                            )}
                          </td>

                          <td>
                            {formatMoney(
                              Math.max(0, saving),
                            )}
                          </td>

                          <td>
                            {formatDate(
                              promotion.start_date,
                            )}
                          </td>

                          <td>
                            {formatDate(
                              promotion.end_date,
                            )}
                          </td>

                          <td>
                            <span
                              className={`promotion-status-badge ${status}`}
                            >
                              {status}
                            </span>
                          </td>

                          <td>
                            <div className="promotion-row-actions">
                              <button
                                type="button"
                                className="promotion-edit-btn"
                                onClick={() =>
                                  editPromotion(
                                    promotion,
                                  )
                                }
                                title="Edit promotion"
                              >
                                ✎
                              </button>

                              <button
                                type="button"
                                className="promotion-delete-btn"
                                onClick={() =>
                                  deletePromotion(
                                    promotion,
                                  )
                                }
                                title="Delete promotion"
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>

            <div className="promotions-mobile-list">
              {filteredPromotions.map(
                (promotion) => {
                  const status =
                    normalizePromotionStatus(
                      promotion,
                    );

                  const saving =
                    Number(
                      promotion.normal_price || 0,
                    ) -
                    Number(
                      promotion.promo_price || 0,
                    );

                  return (
                    <div
                      className="promotion-mobile-card"
                      key={promotion.id}
                    >
                      <div className="promotion-mobile-card-header">
                        <div>
                          <strong>
                            {promotion.product_name ||
                              "Unnamed Product"}
                          </strong>

                          <span>
                            {promotion.barcode || "-"}
                          </span>
                        </div>

                        <span
                          className={`promotion-status-badge ${status}`}
                        >
                          {status}
                        </span>
                      </div>

                      <div className="promotion-mobile-details">
                        <div>
                          <span>Normal Price</span>
                          <strong>
                            {formatMoney(
                              promotion.normal_price,
                            )}
                          </strong>
                        </div>

                        <div>
                          <span>Promo Price</span>
                          <strong>
                            {formatMoney(
                              promotion.promo_price,
                            )}
                          </strong>
                        </div>

                        <div>
                          <span>Saving</span>
                          <strong>
                            {formatMoney(
                              Math.max(0, saving),
                            )}
                          </strong>
                        </div>

                        <div>
                          <span>Dates</span>
                          <strong>
                            {formatDate(
                              promotion.start_date,
                            )}
                            {" – "}
                            {formatDate(
                              promotion.end_date,
                            )}
                          </strong>
                        </div>
                      </div>

                      <div className="promotion-mobile-actions">
                        <button
                          type="button"
                          className="promotion-edit-text-btn"
                          onClick={() =>
                            editPromotion(promotion)
                          }
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className="promotion-delete-text-btn"
                          onClick={() =>
                            deletePromotion(promotion)
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default PromotionsPage;